"use client";

import { motion } from "framer-motion";
import {
  Handshake,
  DollarSign,
  FileText,
  CheckCircle,
  Target,
  TrendingUp,
} from "lucide-react";
import SectionHero from "../components/SectionHero";
import { useEffect } from "react";

const ShipBrokering = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const spServices = [
    "Identification of suitable buyers or sellers",
    "Initial screening and information exchange",
    "Coordination of inspections and condition assessments",
    "Assistance with negotiations, contracts, and closing",
  ];

  const features = [
    {
      icon: Handshake,
      title: "Confidentiality",
      description:
        "Discreet handling of sensitive transactions and negotiations",
    },
    {
      icon: DollarSign,
      title: "Transparency",
      description:
        "Clear communication about vessel condition and market realities",
    },
    {
      icon: Target,
      title: "Realistic Expectations",
      description:
        "Helping both parties reach terms reflecting the vessel's true value",
    },
  ];

  return (
    <div className="pt-20">
      <SectionHero
        title="Ship Brokering"
        subtitle="Sale, Purchase and Long-Term Decisions Around Vessels"
        description="Buying or selling a ship is a high-value decision where information and timing matter. We support both sides with expertise and professionalism."
      />

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
              Sale & Purchase (S&P) Support
            </h2>
            <p className="mx-auto max-w-3xl text-slate-600">
              We handle every step of the vessel transaction with diligence and
              care.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {spServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-slate-50 p-6 text-center"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 mx-auto">
                  <CheckCircle className="h-6 w-6 text-teal-600" />
                </div>
                <p className="text-slate-700">{service}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center md:mb-16"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
              Our Brokering Approach
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-xl bg-white p-8 text-center shadow-lg"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100">
                  <feature.icon className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="mb-3 text-lg font-bold text-slate-900">
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100 p-8"
            >
              <FileText className="mb-4 h-8 w-8 text-teal-600" />
              <h3 className="mb-3 text-lg font-bold text-slate-900">
                Valuations & Advisory
              </h3>
              <p className="text-slate-700">
                Market conditions change. We provide indicative valuations and
                advisory input supported by technical and operational
                understanding.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 p-8"
            >
              <TrendingUp className="mb-4 h-8 w-8 text-blue-600" />
              <h3 className="mb-3 text-lg font-bold text-slate-900">
                Newbuilding Support
              </h3>
              <p className="text-slate-700">
                For expansion projects, we facilitate discussions with yards and
                provide market input on design and specification choices.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 p-8"
            >
              <Handshake className="mb-4 h-8 w-8 text-purple-600" />
              <h3 className="mb-3 text-lg font-bold text-slate-900">
                Demolition Support
              </h3>
              <p className="text-slate-700">
                For vessel disposal decisions, we assist with negotiations and
                enquiries related to demolition sales.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShipBrokering;
